import React, { useState } from 'react';
import { colors, radii } from '../../theme/tokens';
import { Button, Icon } from '../ui';
import SubNav from '../ui/SubNav';
import { useToast } from '../Toast';
import { useConfirm } from '../ConfirmModal';
import { tasksApi } from '../../lib/tasksApi';

const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'user'];

// Configure Project: department settings + task type builder + field library.
const ProjectConfig = ({ departments = [], taskTypes = [], fieldDefs = [], users = [], onChange }) => {
  const [panel, setPanel] = useState('departments');

  return (
    <div>
      <SubNav
        align="start"
        items={[
          { key: 'departments', label: 'Departments', icon: 'account_tree', active: panel === 'departments', onClick: () => setPanel('departments') },
          { key: 'types', label: 'Task Types', icon: 'build', active: panel === 'types', onClick: () => setPanel('types') },
          { key: 'fields', label: 'Detail Fields', icon: 'list_alt', active: panel === 'fields', onClick: () => setPanel('fields') }
        ]}
      />
      <div style={{ paddingTop: 8 }}>
        {panel === 'departments' && <DepartmentSettings departments={departments} users={users} onChange={onChange} />}
        {panel === 'types' && <TaskTypeBuilder departments={departments} taskTypes={taskTypes} fieldDefs={fieldDefs} onChange={onChange} />}
        {panel === 'fields' && <FieldLibrary fieldDefs={fieldDefs} onChange={onChange} />}
      </div>
    </div>
  );
};

// ── Departments ───────────────────────────────────────────────────────────────
const DepartmentSettings = ({ departments, users = [], onChange }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('folder');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await tasksApi.createDepartment({ name: newName.trim(), icon: newIcon.trim() || 'folder' });
      setNewName('');
      setNewIcon('folder');
      onChange && onChange();
      showToast('Department added', 'success');
    } catch (_) {
      showToast('Failed to add department', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveRow = async (dept, name, icon, members) => {
    try {
      await tasksApi.updateDepartment(dept._id, { name, icon, members });
      onChange && onChange();
      showToast('Department updated', 'success');
    } catch (_) {
      showToast('Failed to update department', 'error');
    }
  };

  const remove = async (dept) => {
    const ok = await confirm(`Delete "${dept.name}"? Tasks keep their other departments.`, { danger: true, confirmText: 'Delete' });
    if (!ok) return;
    try {
      await tasksApi.deleteDepartment(dept._id);
      onChange && onChange();
    } catch (_) {
      showToast('Failed to delete department', 'error');
    }
  };

  return (
    <div>
      <div style={styles.rows}>
        {departments.map((d) => <DeptRow key={d._id} dept={d} users={users} onSave={saveRow} onDelete={remove} />)}
        {departments.length === 0 && <div style={styles.muted}>No departments yet.</div>}
      </div>

      <div style={styles.addCard}>
        <div style={styles.addRow}>
          <div style={styles.field}>
            <label style={styles.label}>New department</label>
            <input style={styles.input} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Quality Control" />
          </div>
          <div style={{ ...styles.field, maxWidth: 160 }}>
            <label style={styles.label}>Icon</label>
            <input style={styles.input} value={newIcon} onChange={(e) => setNewIcon(e.target.value)} placeholder="folder" />
          </div>
          <Button variant="primary" size="sm" icon="add" onClick={add} disabled={busy}>Add</Button>
        </div>
        <p style={styles.hint}>Icon is a Material Symbols name (e.g. draw, payments, groups, engineering).</p>
      </div>
    </div>
  );
};

const DeptRow = ({ dept, users = [], onSave, onDelete }) => {
  const [name, setName] = useState(dept.name);
  const [icon, setIcon] = useState(dept.icon || 'folder');
  const [members, setMembers] = useState((dept.members || []).map(String));

  const initialMembers = (dept.members || []).map(String);
  const sameMembers =
    members.length === initialMembers.length && members.every((m) => initialMembers.includes(m));
  const dirty = name !== dept.name || icon !== (dept.icon || 'folder') || !sameMembers;

  const toggleMember = (id) =>
    setMembers((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <div style={styles.deptCard}>
      <div style={styles.deptRow}>
        <Icon name={icon || 'folder'} size={20} color={colors.parGreenBright} />
        <input style={{ ...styles.input, flex: 1, minWidth: 140 }} value={name} onChange={(e) => setName(e.target.value)} />
        <input style={{ ...styles.input, width: 130 }} value={icon} onChange={(e) => setIcon(e.target.value)} />
        <Button variant="ghost" size="sm" icon="save" onClick={() => onSave(dept, name.trim(), icon.trim() || 'folder', members)} disabled={!dirty}>Save</Button>
        <Button variant="danger" size="sm" icon="delete" onClick={() => onDelete(dept)}>Delete</Button>
      </div>
      <div style={styles.memberBlock}>
        <label style={styles.label}>Who can access this department</label>
        <div style={styles.chips}>
          {users.map((u) => {
            const on = members.includes(String(u._id));
            return (
              <button key={u._id} type="button" onClick={() => toggleMember(String(u._id))} style={{ ...styles.chipSm, ...(on ? styles.chipOn : {}) }}>
                {u.name}
              </button>
            );
          })}
          {users.length === 0 && <span style={styles.muted}>No task-enabled users yet. Grant access in Admin → Manage Users.</span>}
        </div>
        <p style={styles.hint}>Rubber users always see every department. Selected users above can also see this one.</p>
      </div>
    </div>
  );
};

// ── Task types (builder) ────────────────────────────────────────────────────
const blankType = { name: '', icon: 'task', departments: [], fields: [] };

const TaskTypeBuilder = ({ departments, taskTypes, fieldDefs = [], onChange }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null); // draft object or null
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(false); // field picker open?

  const startNew = () => { setPicking(false); setEditing({ ...blankType, fields: [] }); };
  const startEdit = (t) => { setPicking(false); setEditing({
    _id: t._id,
    name: t.name,
    icon: t.icon || 'task',
    departments: (t.departments || []).map(String),
    fields: (t.fields || []).map((f) => ({ ...f, departments: (f.departments || []).map(String) }))
  }); };

  const toggleDept = (id) =>
    setEditing((e) => ({ ...e, departments: e.departments.includes(id) ? e.departments.filter((x) => x !== id) : [...e.departments, id] }));

  // Add a field from the library by reference (label/type/options come from the
  // def — single source of truth — so they aren't duplicated).
  const addFieldFromDef = (def) => {
    setEditing((e) => {
      if (e.fields.some((f) => f.key === def.key)) return e; // already on this type
      return { ...e, fields: [...e.fields, { key: def.key, label: def.label, type: def.type, options: def.options || [], departments: [], required: false, order: e.fields.length }] };
    });
    setPicking(false);
  };

  const updateField = (idx, patch) =>
    setEditing((e) => ({ ...e, fields: e.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)) }));

  const removeField = (idx) =>
    setEditing((e) => ({ ...e, fields: e.fields.filter((_, i) => i !== idx) }));

  const toggleFieldDept = (idx, id) =>
    setEditing((e) => ({
      ...e,
      fields: e.fields.map((f, i) =>
        i === idx ? { ...f, departments: f.departments.includes(id) ? f.departments.filter((x) => x !== id) : [...f.departments, id] } : f
      )
    }));

  const save = async () => {
    if (!editing.name.trim()) { showToast('Task type needs a name', 'error'); return; }
    setSaving(true);
    const payload = {
      name: editing.name.trim(),
      icon: editing.icon.trim() || 'task',
      departments: editing.departments,
      // Send references only; the server resolves label/type/options from the library.
      fields: editing.fields.map((f, i) => ({
        key: f.key,
        departments: f.departments,
        required: !!f.required,
        order: i
      }))
    };
    try {
      if (editing._id) await tasksApi.updateTaskType(editing._id, payload);
      else await tasksApi.createTaskType(payload);
      setEditing(null);
      onChange && onChange();
      showToast('Task type saved', 'success');
    } catch (_) {
      showToast('Failed to save task type', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    const ok = await confirm(`Delete task type "${t.name}"?`, { danger: true, confirmText: 'Delete' });
    if (!ok) return;
    try {
      await tasksApi.deleteTaskType(t._id);
      onChange && onChange();
    } catch (_) {
      showToast('Failed to delete task type', 'error');
    }
  };

  const deptName = (id) => departments.find((d) => String(d._id) === String(id))?.name || '—';

  if (editing) {
    return (
      <div>
        <div style={styles.editorHead}>
          <h3 style={styles.h3}>{editing._id ? 'Edit task type' : 'New task type'}</h3>
          <Button variant="subtle" size="sm" icon="arrow_back" onClick={() => setEditing(null)}>Back</Button>
        </div>

        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>Name *</label>
            <input style={styles.input} value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Drawing Request" />
          </div>
          <div style={{ ...styles.field, maxWidth: 160 }}>
            <label style={styles.label}>Icon</label>
            <input style={styles.input} value={editing.icon} onChange={(e) => setEditing((s) => ({ ...s, icon: e.target.value }))} />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Shows in departments</label>
          <div style={styles.chips}>
            {departments.map((d) => {
              const on = editing.departments.includes(String(d._id));
              return (
                <button key={d._id} type="button" onClick={() => toggleDept(String(d._id))} style={{ ...styles.chip, ...(on ? styles.chipOn : {}) }}>
                  <Icon name={d.icon || 'folder'} size={15} color={on ? colors.parGreenBright : colors.text} /> {d.name}
                </button>
              );
            })}
            {departments.length === 0 && <span style={styles.muted}>Add departments first.</span>}
          </div>
        </div>

        <div style={styles.fieldsHead}>
          <label style={styles.label}>Detail fields</label>
          {!picking && <Button variant="ghost" size="sm" icon="add" onClick={() => setPicking(true)}>Add field</Button>}
        </div>
        <p style={styles.hint}>Fields are shared across task types. Reuse one to keep values and analytics linked. Rename or change a field's type under <strong>Detail Fields</strong>.</p>

        {editing.fields.map((f, idx) => (
          <div key={f.key || idx} style={styles.fieldCard}>
            <div style={styles.fieldCardHead}>
              <div style={{ minWidth: 0 }}>
                <div style={styles.fieldCardLabel}>{f.label || f.key}</div>
                <div style={styles.fieldCardType}><Icon name="sell" size={13} color={colors.textFaint} /> {f.type}{f.type === 'select' && (f.options || []).length ? ` · ${(f.options || []).join(', ')}` : ''}</div>
              </div>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} /> Required
              </label>
              <button type="button" onClick={() => removeField(idx)} style={styles.fieldRemove} title="Remove from this task type">
                <Icon name="close" size={18} color={colors.textFaint} />
              </button>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Visible in departments (none = all this type's departments)</label>
              <div style={styles.chips}>
                {departments.map((d) => {
                  const on = f.departments.includes(String(d._id));
                  return (
                    <button key={d._id} type="button" onClick={() => toggleFieldDept(idx, String(d._id))} style={{ ...styles.chipSm, ...(on ? styles.chipOn : {}) }}>
                      {d.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        {editing.fields.length === 0 && !picking && <div style={styles.muted}>No detail fields yet. Click "Add field" to reuse an existing one or create a new one.</div>}

        {picking && (
          <FieldPicker
            fieldDefs={fieldDefs}
            usedKeys={editing.fields.map((f) => f.key)}
            onPick={addFieldFromDef}
            onCreated={(def) => { addFieldFromDef(def); onChange && onChange(); }}
            onClose={() => setPicking(false)}
          />
        )}

        <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
          <Button variant="primary" size="sm" icon="save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save task type'}</Button>
          <Button variant="ghost" size="sm" onClick={() => { setPicking(false); setEditing(null); }}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.toolbar}>
        <h3 style={styles.h3}>Task types</h3>
        <Button variant="primary" size="sm" icon="add" onClick={startNew}>New Task Type</Button>
      </div>
      <div style={styles.builtinNote}>
        <Icon name="task_alt" size={18} color={colors.parGreenBright} />
        <span><strong>General Task</strong> is always available in every department and automatically shows every field used by that department's task types — no template needed.</span>
      </div>
      <div style={styles.rows}>
        {taskTypes.filter((t) => !t.isDefault).map((t) => (
          <div key={t._id} style={styles.typeRow}>
            <Icon name={t.icon || 'task'} size={20} color={colors.parGreenBright} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.typeName}>{t.name}</div>
              <div style={styles.typeMeta}>
                {(t.fields || []).length} field{(t.fields || []).length === 1 ? '' : 's'}
                {' · '}
                {(t.departments || []).length ? (t.departments || []).map(deptName).join(', ') : 'all departments'}
              </div>
            </div>
            <Button variant="ghost" size="sm" icon="edit" onClick={() => startEdit(t)}>Edit</Button>
            <Button variant="danger" size="sm" icon="delete" onClick={() => remove(t)}>Delete</Button>
          </div>
        ))}
        {taskTypes.filter((t) => !t.isDefault).length === 0 && <div style={styles.muted}>No custom task types yet. Use General Task, or create a template here.</div>}
      </div>
    </div>
  );
};

// Picker shown inside the task-type editor: search the field library, click to
// reuse, or create a new field at the bottom (which is added to the library).
const FieldPicker = ({ fieldDefs = [], usedKeys = [], onPick, onCreated, onClose }) => {
  const showToast = useToast();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('text');
  const [newOptions, setNewOptions] = useState('');
  const [busy, setBusy] = useState(false);

  const used = new Set(usedKeys.map(String));
  const available = fieldDefs
    .filter((d) => !d.archived && !used.has(String(d.key)))
    .filter((d) => d.label.toLowerCase().includes(q.trim().toLowerCase()));

  const create = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      const def = await tasksApi.createFieldDef({
        label: newLabel.trim(),
        type: newType,
        options: newType === 'select' ? newOptions.split(',').map((o) => o.trim()).filter(Boolean) : []
      });
      if (def.reused) showToast('Reused an existing field with that name', 'info');
      onCreated && onCreated(def);
      setNewLabel(''); setNewOptions(''); setNewType('text'); setCreating(false);
    } catch (_) {
      showToast('Failed to create field', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.picker}>
      <div style={styles.pickerHead}>
        <input autoFocus style={{ ...styles.input, flex: 1 }} placeholder="Search existing fields…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button variant="ghost" size="sm" icon="close" onClick={onClose}>Close</Button>
      </div>
      <div style={styles.pickerList}>
        {available.map((d) => (
          <button key={d.key} type="button" style={styles.pickerRow} onClick={() => onPick(d)}>
            <Icon name="add_circle" size={18} color={colors.parGreenBright} />
            <span style={styles.pickerRowLabel}>{d.label}</span>
            <span style={styles.pickerRowType}>{d.type}{d.usageCount ? ` · used in ${d.usageCount}` : ''}</span>
          </button>
        ))}
        {available.length === 0 && <div style={styles.muted}>{q ? 'No matching fields.' : 'No reusable fields yet — create one below.'}</div>}
      </div>

      {!creating ? (
        <button type="button" style={styles.createToggle} onClick={() => { setCreating(true); setNewLabel(q); }}>
          <Icon name="add" size={16} color={colors.parGreenBright} /> Create new field{q.trim() ? ` “${q.trim()}”` : ''}
        </button>
      ) : (
        <div style={styles.createForm}>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>New field label</label>
              <input style={styles.input} autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Client name" />
            </div>
            <div style={{ ...styles.field, maxWidth: 170 }}>
              <label style={styles.label}>Type</label>
              <select style={styles.input} value={newType} onChange={(e) => setNewType(e.target.value)}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {newType === 'select' && (
            <div style={styles.field}>
              <label style={styles.label}>Options (comma separated)</label>
              <input style={styles.input} value={newOptions} onChange={(e) => setNewOptions(e.target.value)} placeholder="Low, Medium, High" />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" icon="check" onClick={create} disabled={busy || !newLabel.trim()}>Add field</Button>
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Detail Fields tab (the shared field library) ───────────────────────────────
const FieldLibrary = ({ fieldDefs = [], onChange }) => {
  const showToast = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [options, setOptions] = useState('');
  const [busy, setBusy] = useState(false);

  const visible = fieldDefs.filter((d) => showArchived || !d.archived);

  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const def = await tasksApi.createFieldDef({
        label: label.trim(),
        type,
        options: type === 'select' ? options.split(',').map((o) => o.trim()).filter(Boolean) : []
      });
      if (def.reused) showToast('A field with that name already exists', 'info');
      else showToast('Field added', 'success');
      setLabel(''); setOptions(''); setType('text');
      onChange && onChange();
    } catch (_) {
      showToast('Failed to create field', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={styles.toolbar}>
        <h3 style={styles.h3}>Detail fields</h3>
        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
        </label>
      </div>
      <div style={styles.builtinNote}>
        <Icon name="info" size={18} color={colors.parGreenBright} />
        <span>Fields here are <strong>shared across every task type</strong>. Editing one updates it everywhere it's used. Values stay linked to the field, so tasks can be cross-converted and rolled up in analytics.</span>
      </div>

      <div style={styles.rows}>
        {visible.map((d) => <FieldDefRow key={d._id} def={d} onChange={onChange} />)}
        {visible.length === 0 && <div style={styles.muted}>No fields yet. Add one below.</div>}
      </div>

      <div style={styles.addCard}>
        <div style={styles.addRow}>
          <div style={styles.field}>
            <label style={styles.label}>New field</label>
            <input style={styles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Client number" />
          </div>
          <div style={{ ...styles.field, maxWidth: 160 }}>
            <label style={styles.label}>Type</label>
            <select style={styles.input} value={type} onChange={(e) => setType(e.target.value)}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Button variant="primary" size="sm" icon="add" onClick={add} disabled={busy || !label.trim()}>Add</Button>
        </div>
        {type === 'select' && (
          <div style={{ ...styles.field, marginTop: 8 }}>
            <label style={styles.label}>Options (comma separated)</label>
            <input style={styles.input} value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Low, Medium, High" />
          </div>
        )}
      </div>
    </div>
  );
};

const FieldDefRow = ({ def, onChange }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [label, setLabel] = useState(def.label);
  const [type, setType] = useState(def.type);
  const [options, setOptions] = useState((def.options || []).join(', '));
  const [busy, setBusy] = useState(false);

  const dirty =
    label !== def.label ||
    type !== def.type ||
    (type === 'select' && options !== (def.options || []).join(', '));

  const save = async () => {
    setBusy(true);
    try {
      await tasksApi.updateFieldDef(def._id, {
        label: label.trim(),
        type,
        options: type === 'select' ? options.split(',').map((o) => o.trim()).filter(Boolean) : []
      });
      showToast('Field updated', 'success');
      onChange && onChange();
    } catch (_) {
      showToast('Failed to update field', 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await tasksApi.updateFieldDef(def._id, { archived: !def.archived });
      onChange && onChange();
    } catch (_) {
      showToast('Failed to update field', 'error');
    }
  };

  const remove = async () => {
    const ok = await confirm(`Delete field "${def.label}"? This can't be undone.`, { danger: true, confirmText: 'Delete' });
    if (!ok) return;
    try {
      await tasksApi.deleteFieldDef(def._id);
      onChange && onChange();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to delete field', 'error');
    }
  };

  return (
    <div style={{ ...styles.deptCard, ...(def.archived ? { opacity: 0.6 } : {}) }}>
      <div style={styles.deptRow}>
        <input style={{ ...styles.input, flex: 1, minWidth: 140 }} value={label} onChange={(e) => setLabel(e.target.value)} />
        <select style={{ ...styles.input, width: 130 }} value={type} onChange={(e) => setType(e.target.value)}>
          {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button variant="ghost" size="sm" icon="save" onClick={save} disabled={!dirty || busy}>Save</Button>
        <Button variant="subtle" size="sm" icon={def.archived ? 'unarchive' : 'archive'} onClick={toggleArchive}>{def.archived ? 'Restore' : 'Archive'}</Button>
        <Button variant="danger" size="sm" icon="delete" onClick={remove}>Delete</Button>
      </div>
      {type === 'select' && (
        <div style={{ ...styles.field, marginTop: 8 }}>
          <label style={styles.label}>Options (comma separated)</label>
          <input style={styles.input} value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Low, Medium, High" />
        </div>
      )}
      <div style={styles.fieldUsage}>
        <Icon name="link" size={13} color={colors.textFaint} />
        {def.usageCount
          ? `Used in ${def.usageCount} task type${def.usageCount === 1 ? '' : 's'}${def.usedIn && def.usedIn.length ? ': ' + def.usedIn.join(', ') : ''}`
          : 'Not used by any task type yet'}
        {def.archived ? ' · archived' : ''}
      </div>
    </div>
  );
};

const styles = {
  rows: { display: 'flex', flexDirection: 'column', gap: 10 },
  muted: { color: colors.textFaint, fontSize: 13, padding: '8px 0' },
  hint: { fontSize: 12, color: colors.textFaint, marginTop: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 },
  label: { fontSize: 12, fontWeight: 700, color: colors.textMuted },
  input: {
    padding: '10px 12px', borderRadius: radii.sm, border: `1px solid ${colors.border}`,
    background: colors.inputBg, color: colors.text, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit'
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, alignItems: 'end' },
  addCard: { marginTop: 16, padding: 16, background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md },
  addRow: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
  deptCard: { padding: '12px 14px', background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md },
  deptRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  memberBlock: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.glassBorder}`, display: 'flex', flexDirection: 'column', gap: 8 },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' },
  builtinNote: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', marginBottom: 12, background: 'rgba(47,182,92,0.10)', border: '1px solid rgba(47,182,92,0.35)', borderRadius: radii.md, fontSize: 12.5, color: colors.textMuted, lineHeight: 1.4 },
  editorHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  h3: { margin: 0, fontSize: 17, color: colors.text },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: radii.pill,
    border: `1px solid ${colors.glassBorder}`, background: colors.pill, color: colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer'
  },
  chipSm: {
    display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: radii.pill,
    border: `1px solid ${colors.glassBorder}`, background: colors.pill, color: colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer'
  },
  chipOn: { background: 'rgba(47,182,92,0.18)', border: '1px solid rgba(47,182,92,0.55)', color: colors.parGreenBright },
  fieldsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 0' },
  fieldCard: { padding: 14, background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, marginBottom: 12 },
  typeRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, flexWrap: 'wrap' },
  typeName: { fontWeight: 700, fontSize: 14.5, color: colors.text },
  typeMeta: { fontSize: 12.5, color: colors.textMuted, marginTop: 2 },

  // Field rows inside the task-type editor (reference to a library field).
  fieldCardHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  fieldCardLabel: { fontWeight: 700, fontSize: 14.5, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  fieldCardType: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: colors.textMuted, marginTop: 2 },
  fieldRemove: { marginLeft: 'auto', background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex', borderRadius: radii.sm, flexShrink: 0 },

  // Field picker.
  picker: { marginTop: 8, padding: 12, background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md },
  pickerHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  pickerList: { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto' },
  pickerRow: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
    padding: '9px 10px', borderRadius: radii.sm, border: `1px solid ${colors.glassBorder}`,
    background: colors.pill, color: colors.text, cursor: 'pointer', fontFamily: 'inherit'
  },
  pickerRowLabel: { flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pickerRowType: { fontSize: 12, color: colors.textFaint, flexShrink: 0 },
  createToggle: {
    display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px',
    borderRadius: radii.pill, border: `1px dashed ${colors.parGreen}`, background: 'rgba(47,182,92,0.08)',
    color: colors.parGreenBright, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
  },
  createForm: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.glassBorder}` },
  fieldUsage: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: colors.textFaint }
};

export default ProjectConfig;
